// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react'
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, ToggleButtonGroup, ToggleButton, Snackbar, Alert,
  Checkbox, FormControlLabel, IconButton, Tooltip
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { FkspRequest, FkspRequestType, FkspRequestStatus } from '../types'
import { fkspRowSxIfEconomProcessed, fkspStatusChipColor, fkspStatusLabelCs } from '../lib/fkspStatusLabels'
import { loadFkspRequests, createFkspRequest, downloadFkspPdf, uploadFile, loadPersonalNumber, deleteFkspRequest } from '../lib/apiPhpAdapter'
import { format } from 'date-fns'
import { FkspRequestModal } from '../components/FkspRequestModal'
import { FkspRequestsToolbar } from '../components/FkspRequestsToolbar'
import { filterAndSortFkspRequests } from '../lib/fkspListFilters'
import {
  FKSP_BENEFIT_OPTIONS,
  FKSP_PRIPOJISTENI_MAX_KC,
  FKSP_PRIPOJISTENI_OPTIONS,
  FKSP_PAGE_SIZE,
  FKSP_REQUESTER_FETCH_LIMIT,
  type FkspEkonomSortField,
} from '../lib/fkspConstants'
import { FkspTablePagination, fkspPageSlice } from '../components/FkspTablePagination'
import { canCreateFkspOnBehalf } from '../lib/fkspProxyAccess'
import { canDeleteOnBehalfRequest } from '../lib/onBehalfAccess'
import { EmployeeUserPicker } from '../components/EmployeeUserPicker'
import type { User } from '../types'

export const FkspView: React.FC = () => {
  const { user } = useAuth()
  const [requests, setRequests] = useState<FkspRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [listLoaded, setListLoaded] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [toast, setToast] = useState<{show: boolean, msg: string, type: 'success'|'error'}>({ show: false, msg: '', type: 'success' })
  const [selectedReq, setSelectedReq] = useState<FkspRequest | null>(null)

  const [type, setType] = useState<FkspRequestType>('benefity')
  
  // Form fields
  const [personalNumberForm, setPersonalNumberForm] = useState('')
  const [address, setAddress] = useState('')
  const [benefitType, setBenefitType] = useState(FKSP_BENEFIT_OPTIONS[0])
  const [insuranceType, setInsuranceType] = useState(FKSP_PRIPOJISTENI_OPTIONS[0])
  const [institutionName, setInstitutionName] = useState('')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [descriptionLink, setDescriptionLink] = useState('')
  const [contractNo, setContractNo] = useState('')
  const [contractDate, setContractDate] = useState('')
  const [fkspCont, setFkspCont] = useState('')
  const [accountNo, setAccountNo] = useState('')
  const [varSymbol, setVarSymbol] = useState('')
  const [providerName, setProviderName] = useState('')
  const [providerAddress, setProviderAddress] = useState('')
  const [providerIco, setProviderIco] = useState('')
  const [providerEmail, setProviderEmail] = useState('')
  const [providerPhone, setProviderPhone] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [contractAttachmentUrl, setContractAttachmentUrl] = useState('')
  const [contractAttachmentFilename, setContractAttachmentFilename] = useState('')
  const [contractUploading, setContractUploading] = useState(false)
  const [listSearch, setListSearch] = useState('')
  const [listCreatedFrom, setListCreatedFrom] = useState('')
  const [listCreatedTo, setListCreatedTo] = useState('')
  const [listSortField, setListSortField] = useState<FkspEkonomSortField>('created_at')
  const [listPage, setListPage] = useState(0)
  const [listDateNewestFirst, setListDateNewestFirst] = useState(true)
  const [proxyEmployee, setProxyEmployee] = useState<User | null>(null)

  const canSubmitOnBehalf = canCreateFkspOnBehalf(user)
  const isItAdmin = user?.role === 'admin' && user?.admin_type === 'spravce_it'

  const canDeleteRequest = (req: FkspRequest) => canDeleteOnBehalfRequest(user, req, isItAdmin)

  const handleDeleteRequest = async (id: string) => {
    const ok = window.confirm('Opravdu chcete tuto žádost smazat? Žádost bude přesunuta do archivu smazaných (viditelného pouze správci IT).')
    if (!ok) return
    try {
      setLoading(true)
      await deleteFkspRequest(id)
      setSelectedReq(null)
      setToast({ show: true, msg: 'Žádost byla smazána', type: 'success' })
      await fetchRequests()
    } catch (err: unknown) {
      setToast({
        show: true,
        msg: err instanceof Error ? err.message : 'Mazání se nezdařilo',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRequests()
  }, [])

  const fetchRequests = async () => {
    setLoading(true)
    try {
      const data = await loadFkspRequests('fksp_requester', { limit: FKSP_REQUESTER_FETCH_LIMIT })
      setRequests(data)
    } catch (err: any) {
      setToast({ show: true, msg: 'Chyba při načítání: ' + err.message, type: 'error' })
    } finally {
      setLoading(false)
      setListLoaded(true)
    }
  }

  useDeepLinkById({
    items: requests,
    ready: listLoaded,
    onFound: (r) => setSelectedReq(r),
    onNotFound: () =>
      setToast({ show: true, msg: 'Žádost nenalezena nebo již byla zpracována.', type: 'error' }),
  })

  const clearContractAttachment = () => {
    setContractAttachmentUrl('')
    setContractAttachmentFilename('')
  }

  useEffect(() => {
    if (type === 'benefity') {
      clearContractAttachment()
    }
  }, [type])

  useEffect(() => {
    const targetId = proxyEmployee?.id
    let cancelled = false
    loadPersonalNumber(targetId)
      .then((pn) => {
        if (!cancelled) setPersonalNumberForm(pn ?? '')
      })
      .catch(() => {
        if (!cancelled) setPersonalNumberForm('')
      })
    return () => {
      cancelled = true
    }
  }, [user?.id, proxyEmployee?.id])

  const handleContractFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setContractUploading(true)
    try {
      const r = await uploadFile(file)
      setContractAttachmentUrl(r.file_url)
      setContractAttachmentFilename(r.file_name || file.name)
    } catch (err: unknown) {
      clearContractAttachment()
      setToast({
        show: true,
        msg: err instanceof Error ? err.message : 'Nahrání přílohy se nezdařilo',
        type: 'error',
      })
    } finally {
      setContractUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitLoading(true)
    
    try {
      if (type === 'pripojisteni' && !contractAttachmentUrl.trim()) {
        setToast({
          show: true,
          msg: 'Přiložte sken smlouvy včetně údajů pro platby (příspěvky) ze strany zaměstnavatele.',
          type: 'error',
        })
        setSubmitLoading(false)
        return
      }

      const payload: Partial<FkspRequest> = {
        type,
        requester_name: proxyEmployee?.name ?? user?.name,
        requester_address: address,
        personal_number: personalNumberForm,
        contract_attachment_url: contractAttachmentUrl.trim() || undefined,
        contract_attachment_filename: contractAttachmentFilename.trim() || undefined,
      }

      if (proxyEmployee && proxyEmployee.id !== user?.id) {
        payload.requester_id = proxyEmployee.id
      }
      
      if (type === 'benefity') {
        payload.benefit_type = benefitType
        payload.amount = parseFloat(amount) || undefined
        payload.expenses_description = description
        payload.description_link = descriptionLink
        payload.provider_name = providerName
        payload.provider_address = providerAddress
        payload.provider_ico = providerIco
        payload.provider_email = providerEmail
        payload.provider_phone = providerPhone
      } else {
        const fc = parseFloat(fkspCont)
        if (Number.isNaN(fc) || fc < 0 || fc > FKSP_PRIPOJISTENI_MAX_KC) {
          setToast({
            show: true,
            msg: `Příspěvek FKSP u připojištění: zadejte částku 0–${FKSP_PRIPOJISTENI_MAX_KC} Kč.`,
            type: 'error',
          })
          setSubmitLoading(false)
          return
        }
        payload.benefit_type = insuranceType
        payload.institution_name = institutionName
        payload.contract_number = contractNo
        payload.contract_date = contractDate
        payload.fksp_contribution = fc
        payload.account_number = accountNo
        payload.variable_symbol = varSymbol
      }
      
      await createFkspRequest(payload)
      setToast({ show: true, msg: 'Žádost úspěšně podána', type: 'success' })
      
      // Reset form
      setPersonalNumberForm('')
      setAddress('')
      setAmount('')
      setDescription('')
      setDescriptionLink('')
      setInsuranceType(FKSP_PRIPOJISTENI_OPTIONS[0])
      setInstitutionName('')
      setContractNo('')
      setContractDate('')
      setFkspCont('')
      setAccountNo('')
      setVarSymbol('')
      setProviderName('')
      setProviderAddress('')
      setProviderIco('')
      setProviderEmail('')
      setProviderPhone('')
      setTermsAccepted(false)
      setProxyEmployee(null)
      clearContractAttachment()
      
      fetchRequests()
    } catch (err: any) {
      setToast({ show: true, msg: err.message, type: 'error' })
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleDownloadPdf = async (req: FkspRequest) => {
    try {
      setToast({ show: true, msg: 'Generuji PDF...', type: 'success' })
      await downloadFkspPdf(req.id, req.type)
    } catch (err: any) {
      setToast({ show: true, msg: 'Chyba při stahování PDF: ' + err.message, type: 'error' })
    }
  }

  const displayRequests = useMemo(
    () =>
      filterAndSortFkspRequests(requests, {
        searchQuery: listSearch,
        dateNewestFirst: listDateNewestFirst,
        createdFrom: listCreatedFrom,
        createdTo: listCreatedTo,
        sortField: listSortField,
      }),
    [requests, listSearch, listDateNewestFirst, listCreatedFrom, listCreatedTo, listSortField],
  )

  useEffect(() => {
    setListPage(0)
  }, [listSearch, listDateNewestFirst, listCreatedFrom, listCreatedTo, listSortField])

  const pagedRequests = useMemo(
    () => fkspPageSlice(displayRequests, listPage),
    [displayRequests, listPage],
  )

  const getStatusChip = (status: FkspRequestStatus) => {
    switch(status) {
      case 'new': 
      case 'zocmos_pending':
      case 'personalista_pending':
        return <Chip label="Čeká na schválení" color="warning" size="small" />
      case 'personalista_approved':
      case 'zocmos_approved':
      case 'director_pending':
        return <Chip label="V procesu schvalování" color="info" size="small" />
      case 'director_approved':
        return <Chip label="Schváleno" color="success" size="small" />
      case 'personalista_rejected':
      case 'zocmos_rejected':
      case 'director_rejected':
        return <Chip label="Zamítnuto" color="error" size="small" />
      default:
        return <Chip label={fkspStatusLabelCs(status)} color={fkspStatusChipColor(status)} size="small" />
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        Žádost o příspěvek FKSP
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={3}>Nová žádost</Typography>
              
              <Box mb={3} display="flex" justifyContent="center">
                <ToggleButtonGroup
                  color="primary"
                  value={type}
                  exclusive
                  onChange={(_, val) => val && setType(val)}
                  fullWidth
                >
                  <ToggleButton value="benefity">Benefity</ToggleButton>
                  <ToggleButton value="pripojisteni">Připojištění</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {canSubmitOnBehalf && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Žadatel (podat jménem zaměstnance)
                      </Typography>
                      <EmployeeUserPicker
                        value={proxyEmployee}
                        onChange={setProxyEmployee}
                      />
                      {proxyEmployee && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Žádost bude podána jménem: <strong>{proxyEmployee.name}</strong>
                        </Typography>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      fullWidth
                      label="Osobní číslo"
                      value={personalNumberForm}
                      required
                      InputProps={{ readOnly: true }}
                      helperText={
                        personalNumberForm
                          ? 'Načteno ze seznamu zaměstnanců'
                          : 'Osobní číslo v systému chybí — kontaktujte personalistku'
                      }
                      placeholder="—"
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField 
                      fullWidth label="Bydliště" value={address}
                      onChange={e => setAddress(e.target.value)} required
                      placeholder="Př.: Dlouhá 5, 350 02 Cheb"
                    />
                  </Grid>

                  {type === 'benefity' ? (
                    <>
                      <Grid item xs={12}>
                        <TextField 
                          select fullWidth label="Typ benefitu"
                          value={benefitType} onChange={e => setBenefitType(e.target.value)}
                        >
                          {FKSP_BENEFIT_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth label="Předpokládaná cena (Kč)" type="number"
                          value={amount} onChange={e => setAmount(e.target.value)} required
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth label="Doplatím na účet zaměstnavatele (č.účtu 107-21636331/0100) v Kč"
                          value={Math.max(0, (parseFloat(amount) || 0) - 4000)}
                          InputProps={{ readOnly: true }}
                          helperText="Automaticky vypočítaná hodnota doplatku (při překročení max. příspěvku 4 000 Kč)"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth label="Předmět žádosti (specifikace)" multiline rows={2}
                          value={description} onChange={e => setDescription(e.target.value)} required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth label="Popis (odkaz)"
                          value={descriptionLink} onChange={e => setDescriptionLink(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} mt={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Poskytovatel služby/zboží</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="Název subjektu"
                          value={providerName} onChange={e => setProviderName(e.target.value)} required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="IČ"
                          value={providerIco} onChange={e => setProviderIco(e.target.value)} required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth label="Adresa dodavatele"
                          value={providerAddress} onChange={e => setProviderAddress(e.target.value)} required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="Email" type="email"
                          value={providerEmail} onChange={e => setProviderEmail(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="Telefon"
                          value={providerPhone} onChange={e => setProviderPhone(e.target.value)}
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <TextField 
                          select fullWidth label="Typ připojištění"
                          value={insuranceType} onChange={e => setInsuranceType(e.target.value)}
                        >
                          {FKSP_PRIPOJISTENI_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth label="Název instituce"
                          value={institutionName} onChange={e => setInstitutionName(e.target.value)} required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="Číslo smlouvy"
                          value={contractNo} onChange={e => setContractNo(e.target.value)} required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="Datum uzavření smlouvy" type="date"
                          value={contractDate} onChange={e => setContractDate(e.target.value)} required
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth label="Příspěvek FKSP (Kč)" type="number"
                          value={fkspCont} onChange={e => setFkspCont(e.target.value)} required
                          inputProps={{ min: 0, max: FKSP_PRIPOJISTENI_MAX_KC, step: 1 }}
                          helperText={`Maximálně ${FKSP_PRIPOJISTENI_MAX_KC} Kč podle Zásad FKSP (připojištění).`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="Číslo účtu"
                          value={accountNo} onChange={e => setAccountNo(e.target.value)} required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField 
                          fullWidth label="Variabilní symbol"
                          value={varSymbol} onChange={e => setVarSymbol(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Příloha — sken smlouvy *
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Nahrajte sken smlouvy včetně údajů pro platby (příspěvky) ze strany zaměstnavatele.
                          Povolené formáty: PDF, JPG, PNG (max. velikost dle nastavení serveru).
                        </Typography>
                        <Button variant="outlined" component="label" disabled={contractUploading || submitLoading}>
                          {contractUploading ? 'Nahrávání…' : contractAttachmentUrl ? 'Změnit soubor' : 'Vybrat soubor'}
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                            onChange={handleContractFileChange}
                          />
                        </Button>
                        {contractAttachmentFilename && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Nahraný soubor: <strong>{contractAttachmentFilename}</strong>
                            <Button size="small" sx={{ ml: 1 }} onClick={clearContractAttachment}>
                              Odebrat
                            </Button>
                          </Typography>
                        )}
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} mt={2}>
                    <Button 
                      type="submit" variant="contained" color="primary" fullWidth size="large"
                      disabled={
                        submitLoading ||
                        contractUploading ||
                        !termsAccepted ||
                        (type === 'pripojisteni' && !contractAttachmentUrl.trim())
                      }
                    >
                      Podat žádost
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: termsAccepted ? 'success.light' : 'grey.300' }}>
                      <FormControlLabel
                        control={<Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} required />}
                        label={
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Prohlášení žadatele (potvrďte souhlas):
                          </Typography>
                        }
                      />
                      <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1, ml: 4, mb: 1 }}>
                        Vyplněním a odesláním tohoto formuláře závazně povrzuji a čestně prohlašuji, že:
                        <br/>- jsem v této žádosti uvedl přesné, úplné a pravdivé údaje,
                        <br/>- že jsem se seznámil s platnými Zásadami tvorby a používání FKSP organizace Integrované střední školy Cheb, příspěvkové organizace, a splňuji je,
                        <br/>- jsem si vědom(a) toho, že pokud by mnou uvedené údaje byly nepravdivé, příp. pokud by se podmínky z mé strany změnily aniž bych o tom informoval(a) organizaci, budu čelit všem z toho vyplývajícím právním či finančním následkům,
                        <br/>- jsem si vědom(a) povinnosti uhradit předem rozdíl mezi celkovými náklady a příspěvkem dle podmínek Zásad na účet FKSP, popř. při neuskutečněné rekreaci vrátím příspěvek na účet FKSP.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" mb={2}>Podané žádosti</Typography>
              <FkspRequestsToolbar
                search={listSearch}
                onSearchChange={setListSearch}
                createdFrom={listCreatedFrom}
                onCreatedFromChange={setListCreatedFrom}
                createdTo={listCreatedTo}
                onCreatedToChange={setListCreatedTo}
                sortField={listSortField}
                onSortFieldChange={setListSortField}
                dateNewestFirst={listDateNewestFirst}
                onDateSortChange={setListDateNewestFirst}
                disabled={loading}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Filtrováno {displayRequests.length} z {requests.length} žádostí
                {displayRequests.length > FKSP_PAGE_SIZE
                  ? ` (po ${FKSP_PAGE_SIZE} na stránku)`
                  : ''}
                .
              </Typography>
              <TableContainer component={Paper} elevation={0} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Datum</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell align="right">Částka</TableCell>
                      <TableCell>Stav</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          {loading ? 'Načítání...' : 'Zatím nemáte žádné žádosti'}
                        </TableCell>
                      </TableRow>
                    ) : displayRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          Žádná žádost neodpovídá vyhledávání
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedRequests.map(req => (
                        <TableRow 
                          key={req.id} 
                          hover 
                          onClick={() => setSelectedReq(req)}
                          sx={{ cursor: 'pointer', ...fkspRowSxIfEconomProcessed(req) }}
                        >
                          <TableCell>{format(new Date(req.created_at), 'dd.MM.yyyy')}</TableCell>
                          <TableCell>
                            {req.type === 'benefity' ? 'Benefity' : 'Připojištění'}
                            <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                              {req.type === 'benefity' ? req.benefit_type?.split('(')[0] : req.institution_name?.split('(')[0]}
                            </Typography>
                            {req.created_by_name && req.created_by_id !== req.requester_id && (
                              <Typography variant="caption" display="block" color="info.main">
                                Podal(a): {req.created_by_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {req.type === 'benefity' ? req.amount : req.fksp_contribution} Kč
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {getStatusChip(req.status)}
                              {req.financist_processed_at && (
                                <Chip label="Zpracováno ekonomem" size="small" sx={{ bgcolor: '#fff59d' }} />
                              )}
                              <Box sx={{ display: 'inline-flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                                <Tooltip title="Stáhnout PDF pro tisk (schválení se v PDF uvede až po dokončení)">
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleDownloadPdf(req)}
                                    sx={{ bgcolor: 'secondary.50', '&:hover': { bgcolor: 'secondary.100' } }}
                                  >
                                    <PictureAsPdfIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <FkspTablePagination
                count={displayRequests.length}
                page={listPage}
                onPageChange={setListPage}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={toast.show} autoHideDuration={6000} onClose={() => setToast({ ...toast, show: false })}>
        <Alert severity={toast.type} onClose={() => setToast({ ...toast, show: false })}>{toast.msg}</Alert>
      </Snackbar>

      <FkspRequestModal
        open={selectedReq !== null}
        onClose={() => setSelectedReq(null)}
        request={selectedReq}
        canApprove={false}
        onApprove={() => {}}
        onReject={() => {}}
        onDelete={selectedReq && canDeleteRequest(selectedReq) ? handleDeleteRequest : undefined}
        onDownloadPdf={handleDownloadPdf}
      />
    </Box>
  )
}
